import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Check, ChevronRight, Minus, Search } from 'lucide-react';

const ACTIONS = [
  { key: 'view', label: 'View' },
  { key: 'create', label: 'Add' },
  { key: 'edit', label: 'Edit' },
  { key: 'delete', label: 'Delete' },
];
const KEYS = ACTIONS.map((a) => a.key);

/**
 * The checklist a role is built from.
 *
 * One card per area of the shop — Billing, Inventory, Customers — rather than
 * one long shared table. Each card is a small table in its own right, with its
 * own heading, its own count and its own column headers, so "give them
 * everything in Billing" is answered inside the card that says Billing on it
 * instead of by finding the right stretch of a list three dozen rows long.
 *
 * The cards fold, and the count on each says what is inside a shut one. Every
 * area open on arrival is thirty-six rows and a hundred and eighteen boxes,
 * which is a wall rather than a choice; a card opens when the role already
 * reaches into that area, so an existing role shows what it can do and keeps
 * quiet about what it cannot. A brand-new role has nothing anywhere, and opens
 * everything instead — the fold summarises a role that exists, it does not hide
 * the page from somebody who has not started.
 *
 * Four boxes across each row: view, add, edit, delete. The shape is the point —
 * a role is easiest to reason about read down a column ("who can delete
 * anything?") as much as across ("what can a Cashier do?").
 *
 * A box the feature does not support is drawn as an empty cell rather than a
 * greyed-out checkbox. A report cannot be deleted; offering the tick and then
 * refusing it teaches people that the ticks do not mean much.
 */

// Two different reasons a box cannot be clicked, and they must not look alike.
//
// `unsupported` means the screen has no such action — a report cannot be
// deleted — and is drawn as an empty dashed cell, because there is nothing
// there to be on or off.
//
// `readOnly` means this role cannot be edited, but what it grants still has to
// be legible. Drawing those as dashed cells hid every tick on a built-in role,
// so the answer to "what can an Organization Admin do?" was a grid of blanks.
// Read-only keeps the tick and drops only the interaction.
const Box = ({ state, onClick, unsupported, readOnly, label }) => {
  if (unsupported) {
    return (
      <span
        className="block w-[17px] h-[17px] mx-auto rounded border border-dashed border-slate-200"
        aria-hidden="true"
      />
    );
  }
  const on = state === 'on';
  const some = state === 'some';
  const fill = on || some;

  if (readOnly) {
    return (
      <span
        role="checkbox"
        aria-checked={on}
        aria-disabled="true"
        aria-label={label}
        className={`block w-[17px] h-[17px] mx-auto rounded border grid place-items-center ${
          fill ? 'text-white border-transparent' : 'bg-slate-50 border-slate-200'
        }`}
        style={fill ? { background: 'var(--theme-primary)' } : undefined}
      >
        {on && <Check size={11} strokeWidth={3.4} />}
        {some && <Minus size={11} strokeWidth={3.4} />}
      </span>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      aria-checked={on}
      role="checkbox"
      className={`block w-[17px] h-[17px] mx-auto rounded border grid place-items-center transition-colors ${
        fill
          ? 'text-white border-transparent'
          : 'bg-white border-slate-300 hover:border-[var(--theme-primary)]'
      }`}
      style={fill ? { background: 'var(--theme-primary)' } : undefined}
    >
      {on && <Check size={11} strokeWidth={3.4} />}
      {some && <Minus size={11} strokeWidth={3.4} />}
    </button>
  );
};
Box.propTypes = {
  state: PropTypes.string,
  onClick: PropTypes.func,
  unsupported: PropTypes.bool,
  readOnly: PropTypes.bool,
  label: PropTypes.string,
};

export const PermissionChecklist = ({ groups, value, onChange, readOnly }) => {
  const [query, setQuery] = useState('');
  // Open where there is something to see. Overrides are per area and only
  // exist once the reader has clicked one, so searching can open everything
  // without forgetting what they had opened by hand.
  const [override, setOverride] = useState({});

  const get = (key, action) => !!(value[key] && value[key][action]);

  const setOne = (key, action, on) => {
    const next = { ...value, [key]: { ...(value[key] || {}), [action]: on } };
    // Ticking anything implies being able to see it. A role that may edit a
    // product but not view one cannot reach the screen to do it, so the pair is
    // not a state worth being able to save.
    if (on && action !== 'view') next[key].view = true;
    // And losing sight of a screen loses the rest with it.
    if (!on && action === 'view') {
      next[key] = { view: false, create: false, edit: false, delete: false };
    }
    onChange(next);
  };

  const groupState = (items, action) => {
    const able = items.filter((f) => f.actions.includes(action));
    if (able.length === 0) return 'off';
    const on = able.filter((f) => get(f.key, action)).length;
    if (on === 0) return 'off';
    return on === able.length ? 'on' : 'some';
  };

  const setGroup = (items, action, on) => {
    const next = { ...value };
    items.forEach((f) => {
      if (!f.actions.includes(action)) return;
      const cur = { ...(next[f.key] || {}) };
      cur[action] = on;
      if (on && action !== 'view') cur.view = true;
      if (!on && action === 'view') { cur.create = false; cur.edit = false; cur.delete = false; }
      next[f.key] = cur;
    });
    onChange(next);
  };

  // How many of the boxes a group actually offers are ticked. "3 of 8" says
  // more than a tick would: it distinguishes a group barely touched from one
  // nearly complete, which is the difference a reviewer is looking for.
  const tally = (items) => {
    let on = 0;
    let able = 0;
    items.forEach((f) => f.actions.forEach((a) => {
      able += 1;
      if (get(f.key, a)) on += 1;
    }));
    return { on, able };
  };

  const q = query.trim().toLowerCase();
  const shown = useMemo(() => {
    if (!q) return groups;
    return groups
      .map((g) => ({
        ...g,
        items: g.items.filter((f) =>
          f.label.toLowerCase().includes(q) || g.group.toLowerCase().includes(q)),
      }))
      .filter((g) => g.items.length > 0);
  }, [groups, q]);

  const total = useMemo(
    () => Object.values(value).reduce((n, a) => n + KEYS.filter((k) => a[k]).length, 0),
    [value],
  );

  const areasTouched = groups.filter((g) => tally(g.items).on > 0).length;

  // A card shows its rows when the role already reaches into that area, and
  // stays shut otherwise. Every area open at once is thirty-six rows and a
  // hundred and eighteen boxes on arrival, which is a wall rather than a
  // choice. A brand-new role has nothing granted anywhere, so it opens
  // everything instead — the fold is meant to summarise a role that exists,
  // not to hide the page from somebody who has not started.
  const blank = total === 0;
  const isOpen = (g) => {
    if (q) return true;
    if (g.group in override) return override[g.group];
    return blank || tally(g.items).on > 0;
  };
  const allOpen = shown.every(isOpen);
  const toggleAll = () => {
    const next = {};
    shown.forEach((g) => { next[g.group] = !allOpen; });
    setOverride(next);
  };

  // Past a handful of panels, the controls that act on all of them belong in
  // one place. Thirteen areas otherwise means thirteen trips to grant the one
  // thing almost every role starts with: being able to look.
  const viewEverywhere = () => {
    const next = { ...value };
    groups.forEach((g) => g.items.forEach((f) => {
      if (f.actions.includes('view')) next[f.key] = { ...(next[f.key] || {}), view: true };
    }));
    onChange(next);
  };
  const clearAll = () => onChange({});

  return (
    <div className="flex flex-col gap-3">
      {/* ── Toolbar ─────────────────────────────────────────────────────── */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[150px] max-w-[220px]">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" aria-hidden="true" />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Find a screen"
            aria-label="Find a screen"
            className="w-full h-[30px] bg-white border border-slate-200 rounded-md pl-7 pr-3 text-mini text-[#0d0d0d] placeholder:text-slate-400 outline-none transition-colors focus:border-[var(--theme-primary)] focus:ring-3 focus:ring-[var(--theme-primary-soft)]"
          />
        </div>
        <span className="text-mini tabular-nums text-slate-500">
          <b className="font-semibold text-[#0d0d0d]">{areasTouched}</b> of {groups.length} areas
        </span>
        {!readOnly && (
          <span className="ml-auto flex items-center gap-1.5">
            <button
              type="button"
              onClick={viewEverywhere}
              className="h-[30px] px-2.5 rounded-md border border-slate-200 bg-white text-mini font-medium text-slate-600 hover:bg-slate-50 hover:text-[#0d0d0d] transition-colors"
            >
              Let them see everything
            </button>
            <button
              type="button"
              onClick={clearAll}
              disabled={total === 0}
              className="h-[30px] px-2.5 rounded-md text-mini font-medium text-slate-500 hover:bg-slate-50 hover:text-[#0d0d0d] transition-colors disabled:opacity-40"
            >
              Clear all
            </button>
          </span>
        )}
        <button
          type="button"
          onClick={toggleAll}
          className={`h-[30px] px-2.5 rounded-md text-mini font-medium text-slate-500 hover:bg-slate-50 hover:text-[#0d0d0d] transition-colors ${readOnly ? 'ml-auto' : ''}`}
        >
          {allOpen ? 'Collapse all' : 'Expand all'}
        </button>
      </div>

      {/* ── One card per area ───────────────────────────────────────────── */}
      <div className="grid grid-cols-1 xl:grid-cols-2 2xl:grid-cols-3 gap-2.5 items-start">
        {shown.map((g) => {
          const { on, able } = tally(g.items);
          const open = isOpen(g);
          return (
            <section
              key={g.group}
              className={`border rounded-md overflow-hidden transition-colors ${
                on > 0 ? 'border-[var(--theme-primary-tint)]' : 'border-slate-200'
              }`}
            >
              <table className="w-full border-collapse">
                <caption
                  className={`border-b text-left ${
                    on > 0
                      ? 'bg-[var(--theme-primary-softer)] border-[var(--theme-primary-tint)]'
                      : 'bg-white border-slate-100'
                  }`}
                >
                  {/* The heading is the fold. A real button, so it can be
                      tabbed to and answers Enter — a click handler on the row
                      could not. */}
                  <button
                    type="button"
                    onClick={() => setOverride((o) => ({ ...o, [g.group]: !open }))}
                    aria-expanded={open}
                    className="w-full flex items-center gap-2 px-3 py-2 text-left"
                  >
                    <ChevronRight
                      size={13}
                      aria-hidden="true"
                      className={`text-slate-400 shrink-0 transition-transform ${open ? 'rotate-90' : ''}`}
                    />
                    <span className="text-body leading-[18px] font-bold text-[#0d0d0d] mr-auto">
                      {g.group}
                    </span>
                    {on > 0 ? (
                      <span
                        className="h-5 px-2 rounded-full text-mini leading-5 font-semibold tabular-nums text-white"
                        style={{ background: 'var(--theme-primary)' }}
                      >
                        {on} of {able}
                      </span>
                    ) : (
                      <span className="h-5 px-2 rounded-full bg-slate-100 text-slate-500 text-mini leading-5 font-semibold tabular-nums">
                        {able}
                      </span>
                    )}
                  </button>
                </caption>

                <thead hidden={!open}>
                  <tr className="bg-slate-50 border-b border-slate-100">
                    <th scope="col" className="text-left px-3 py-1.5 text-micro font-medium text-slate-400">
                      Screen
                    </th>
                    {/* The column header is also the switch for that column in
                        this area. Naming it twice — once as a label, once as a
                        "select all" — would be two controls for one idea. */}
                    {ACTIONS.map((a) => {
                      const able2 = g.items.some((f) => f.actions.includes(a.key));
                      const st = groupState(g.items, a.key);
                      return (
                        <th key={a.key} scope="col" className="w-[40px] px-1 py-1.5">
                          {able2 && !readOnly ? (
                            <button
                              type="button"
                              onClick={() => setGroup(g.items, a.key, st !== 'on')}
                              className={`w-full text-micro font-semibold transition-colors hover:underline ${
                                st === 'off' ? 'text-slate-500 hover:text-[#0d0d0d]' : 'text-[var(--theme-primary)]'
                              }`}
                              aria-label={`${st === 'on' ? 'Remove' : 'Give'} ${a.label} for every screen in ${g.group}`}
                            >
                              {a.label}
                            </button>
                          ) : (
                            <span className="block text-micro font-semibold text-slate-400">{a.label}</span>
                          )}
                        </th>
                      );
                    })}
                  </tr>
                </thead>

                <tbody hidden={!open}>
                  {g.items.map((f) => (
                    <tr key={f.key} className="border-b border-slate-50 last:border-0 hover:bg-slate-50/70 transition-colors">
                      {/* A row header, not a cell: in a matrix the screen name
                          is what each box in the row is about, and marking it
                          up as a header is what lets a screen reader say
                          "Stock, Delete, checked" instead of just "checked". */}
                      <th
                        scope="row"
                        className="text-left font-normal px-3 py-1 text-body text-slate-700 max-w-0 truncate"
                        title={f.label}
                      >
                        {f.label}
                      </th>
                      {ACTIONS.map((a) => (
                        <td key={a.key} className="px-1 py-1">
                          <Box
                            state={get(f.key, a.key) ? 'on' : 'off'}
                            unsupported={!f.actions.includes(a.key)}
                            readOnly={readOnly}
                            label={`${a.label} ${f.label}`}
                            onClick={() => setOne(f.key, a.key, !get(f.key, a.key))}
                          />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          );
        })}
      </div>

      {shown.length === 0 && (
        <p className="text-center text-mini text-slate-400 py-8">
          No screen matches “{query}”.
        </p>
      )}
    </div>
  );
};

PermissionChecklist.propTypes = {
  groups: PropTypes.array.isRequired,
  value: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  readOnly: PropTypes.bool,
};
